/**
 * The other people, live: one socket to the island you are standing on, and the bodies it feeds.
 *
 * Two jobs that are easier to reason about together than apart. The link decides when to talk and
 * when to give up; the roster decides what a stream of positions looks like on screen. They share
 * one clock, and the engine drives both by calling `advance` once a frame.
 *
 * ## The promise this file makes
 *
 * If the socket never opens, or opens and then dies, or opens onto an island where nobody else is
 * standing, the map is exactly the map it was before this feature existed. Nothing spins, nothing
 * announces a connection, nothing says the island is empty. Members of this community are on phones
 * on Serbian mobile networks — the same reason `api.ts` queues every mutation — and a live feature
 * that degrades loudly would be worse than no live feature at all.
 *
 * ## Why the bodies are not simply drawn where the last packet said
 *
 * A member walks one tile per step and the room relays each step as it happens. Painting each
 * arrival immediately would teleport a body sixteen pixels at a time, six times a second, with a
 * stutter every time a packet was late — which on a phone is often. So steps go into a short queue
 * and are walked, at the same pace and with the same gait as your own character. A body that falls
 * behind hurries; one that falls a long way behind stops pretending and simply appears where it is.
 */

import {
  GESTURE_MIN_MS,
  KEEPALIVE_FRAME,
  KEEPALIVE_MS,
  SOBA_VERSION,
  decodeFromRoom,
  encode,
  facingIndex,
  gestOf,
  walkable,
  type Anchor,
  type Facing,
  type Gest,
  type KrajReason,
  type Present,
} from '../../core/soba';
import { STEP_MS } from './gait';

/**
 * Least time between two position frames leaving this browser.
 *
 * Comfortably above the room's own floor of 90 ms, so a step is never dropped for arriving early.
 * The throttle keeps a trailing edge: whatever was suppressed is sent when the window opens, which
 * is what stops a walk ending one tile short of where its owner is standing.
 */
const SEND_MIN_MS = 120;

/** Steps a body may be behind before it starts hurrying, and before it gives up and appears. */
const HURRY_AT = 3;
const SNAP_AT = 6;

/** How long a gesture floats over somebody's head. Long enough to read, short enough to miss. */
const GESTURE_MS = 2_600;

/** Reconnect delay, doubling, jittered. Capped low enough that coming back feels immediate. */
const BACKOFF_MIN_MS = 700;
const BACKOFF_MAX_MS = 20_000;

/** Somebody else, on the map, moving. World tiles — the anchor has already been applied. */
export interface LiveBody {
  id: string;
  name: string;
  look: number;
  souvenirs: readonly string[];
  nodeId: string | null;
  /** The tile being left and the tile being entered; equal when standing. */
  fromX: number;
  fromY: number;
  tileX: number;
  tileY: number;
  /** 0 → 1 across the current step. */
  progress: number;
  facing: Facing;
  /** Alternates every step, so the walk cycle leads with the other leg. */
  stepCount: number;
  gesture: { gest: Gest; until: number } | null;
}

interface Body extends LiveBody {
  queue: Array<{ x: number; y: number; facing: Facing }>;
}

/**
 * There is deliberately no "when did we last hear from them" field here, and adding one is the
 * mistake this note exists to prevent.
 *
 * It reads as obviously right — forget anybody who has gone quiet — and it deletes exactly the
 * wrong people. A member standing still reading a task sends nothing at all; the engine reports a
 * position only when one changes. So a timer keyed on silence removes the people doing what the
 * curriculum asked, and it removes them permanently: the room announces an arrival once, so nobody
 * dropped this way ever comes back.
 *
 * Leaving is the room's job. A socket that closes fires `webSocketClose`; one that dies silently
 * stops answering the keepalive and the edge closes it within a couple of minutes, running the same
 * handler. The only case left over is a body standing where somebody left it — which is precisely
 * what the standing crew has always looked like.
 */

/** Anything that behaves like a browser WebSocket. Injected so the roster is testable in node. */
export interface SocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  readyState: number;
  onopen: ((event: unknown) => void) | null;
  onclose: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
}

export interface UzivoOptions {
  open(url: string): SocketLike;
  /** Wall clock, injectable so the queue behaviour can be tested without waiting for real seconds. */
  now?(): number;
  /** Where the Worker is. Defaults to the page's own origin; named so this class runs outside one. */
  origin?: string;
  /** A challenge landed while this member was standing on the island. See `onDuel` below. */
  onDuel?(duelId: string): void;
}

export class Uzivo {
  private bodies = new Map<string, Body>();
  private socket: SocketLike | null = null;
  private islandId: string | null = null;
  /**
   * The personal island this socket is on, if it is on one.
   *
   * Mutually exclusive with `islandId`: a member is standing on the archipelago or on somebody's own
   * island, never both, and the two are different room kinds with different identities.
   */
  private dom: { ownerId: string; rev: number } | null = null;
  private anchor: Anchor = { x: 0, y: 0 };
  private me: string | null = null;

  /**
   * The most recent pose the engine reported, and the most recent one actually written.
   *
   * `latest` is never cleared, and that is the point: when the room wakes up and asks where
   * everybody is, the answer is almost always a position that has not changed since it was last
   * sent. A design that only remembered unsent work would have nothing to say, and everybody would
   * stay invisible until they next happened to move.
   */
  private latest: { x: number; y: number; f: number; m: 0 | 1 } | null = null;
  private sent: { x: number; y: number; f: number; m: 0 | 1 } | null = null;
  /** Null until something has been sent on this socket — the first frame is never made to wait. */
  private sentAt: number | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private keepaliveTimer: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;

  /** Which curriculum this room is for. Part of the room's identity — see `enter`. */
  private version = 0;

  /**
   * Set when the room has said not to come back to this island.
   *
   * Locked, out of the crew, or replaced by another tab: all of them mean stop asking, which is the
   * whole reason the room answers over the socket rather than with an error — a plain error
   * response to an upgrade never reaches the browser, so every refusal would look like a network
   * drop and be retried forever.
   *
   * It clears on the next `enter` — a different island, or the same one under new content. It is
   * deliberately not permanent for the life of the tab: a member whose progress write is still in
   * the offline queue can be refused an island their own screen already draws as open, and once the
   * queue drains they should be let in without reloading.
   */
  private blocked = false;
  /** Set when this build is older than the room. Nothing will work again until the page reloads. */
  private stale = false;

  private readonly open: (url: string) => SocketLike;
  private readonly clock: () => number;
  private readonly origin: string;
  /**
   * Something happened to a duel while this member was standing on the island.
   *
   * The only thing that reaches the map this way is a challenge — everything after acceptance is
   * relayed into the arena instead. It is a nudge and not the truth: the same duel is in the next
   * state response either way, and the reason it comes over the socket at all is that a challenge
   * is almost always issued by somebody standing next to you, and finding out on a poll twenty
   * seconds later is not the same moment.
   */
  private onDuel: ((duelId: string) => void) | null = null;

  constructor(options: UzivoOptions) {
    this.open = options.open;
    this.clock = options.now ?? (() => Date.now());
    this.origin = options.origin ?? (typeof location === 'undefined' ? '' : location.origin);
    this.onDuel = options.onDuel ?? null;
  }

  /* ---------------------------------------------------------------- the link */

  /**
   * Stand on this island.
   *
   * Called on landfall and whenever the map is rebuilt — the anchor is the island's centre in the
   * *current* world's coordinates, and that moves whenever a new island comes into view.
   */
  enter(islandId: string, anchor: Anchor, contentVersion: number): void {
    this.reanchor(anchor);
    // The version is part of which room this is — the server puts it in the room's name, because a
    // publish can move the trail through an island and two members holding different curricula
    // would be describing different ground. A tab that refreshes its content in place (which the
    // store does, deliberately, without reloading the page) has to be moved across with it.
    if (this.islandId === islandId && this.version === contentVersion && !this.dom) return;
    this.islandId = islandId;
    this.version = contentVersion;
    this.dom = null;
    this.rejoin();
  }

  /**
   * Stand on somebody's own island — theirs or your own.
   *
   * A second room kind, and the only thing that differs is the query the handshake carries and what
   * decides identity. An island's room is `(island, contentVersion)`; a personal island's is
   * `(owner, rev)`, because the owner rearranging is exactly the same class of event as a publish:
   * the ground moves, and two people describing different ground at each other is the one failure
   * the room names exist to prevent.
   *
   * The anchor is `{0,0}` and that is correct rather than lazy. The offset transform exists only
   * because `buildWorld` lays the archipelago out from the islands the viewer can see, so its origin
   * slides per viewer — a personal island has no archipelago and one grid, so a tile is a tile.
   */
  enterDom(ownerId: string, rev: number): void {
    this.reanchor({ x: 0, y: 0 });
    if (this.dom?.ownerId === ownerId && this.dom.rev === rev) return;
    this.islandId = null;
    this.dom = { ownerId, rev };
    this.rejoin();
  }

  private rejoin(): void {
    this.bodies.clear();
    this.disconnect();
    this.attempt = 0;
    this.blocked = false;
    this.connect();
  }

  /**
   * Move the island's centre.
   *
   * Everybody already on the island moves with it. Bodies are stored in world tiles — that is what
   * the renderer wants sixty times a second — so a change of origin has to be applied to them as
   * well as to the conversion, or the whole room shifts sideways by however far the world moved and
   * anybody standing still stays wrong until they take a step.
   *
   * The origin does move: `buildWorld` lays the archipelago out from the islands the viewer can
   * see, so the day an island appears to the north or west of the ones already drawn, every
   * coordinate on the map slides.
   */
  reanchor(anchor: Anchor): void {
    const dx = anchor.x - this.anchor.x;
    const dy = anchor.y - this.anchor.y;
    this.anchor = anchor;
    if (dx === 0 && dy === 0) return;
    for (const body of this.bodies.values()) {
      body.fromX += dx;
      body.fromY += dy;
      body.tileX += dx;
      body.tileY += dy;
      for (const step of body.queue) {
        step.x += dx;
        step.y += dy;
      }
    }
  }

  /** Which island's room this is, or null before the first one has been joined. */
  room(): string | null {
    return this.islandId;
  }

  leave(): void {
    this.islandId = null;
    this.dom = null;
    this.bodies.clear();
    this.disconnect();
  }

  private connect(): void {
    if (this.stale || this.blocked) return;
    if (!this.islandId && !this.dom) return;
    if (this.socket) return;

    const where = this.dom
      ? `dom=${encodeURIComponent(this.dom.ownerId)}&rev=${this.dom.rev}`
      : `ostrvo=${encodeURIComponent(this.islandId!)}`;
    const url = `${this.origin.replace(/^http/, 'ws')}/api/soba/ws?${where}&v=${SOBA_VERSION}`;

    let socket: SocketLike;
    try {
      socket = this.open(url);
    } catch {
      // Some browsers throw on `new WebSocket` outright — a blocked scheme, a torn-down page. The
      // map does not care; it just never gets anybody.
      this.retry();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      // The room has never heard from this socket, so nothing it was told before counts.
      this.sent = null;
      this.sentAt = null;
      this.flush(true);
    };
    socket.onmessage = (event) => this.receive(event.data);
    socket.onerror = () => {
      /* the close that follows is where the retry is decided */
    };
    socket.onclose = () => {
      if (this.socket === socket) {
        this.socket = null;
        this.bodies.clear();
        this.retry();
      }
    };

    this.keepaliveTimer ??= setInterval(() => {
      // Silence for long enough and the edge closes the socket. A standing member sends nothing, so
      // without this the map would quietly lose everybody every couple of minutes. The room answers
      // this without waking up.
      // Written literally rather than through `encode`: the room answers this exact string with
      // `setWebSocketAutoResponse`, so a byte out of place would wake it every thirty seconds
      // instead of never.
      try {
        if (this.socket?.readyState === 1) this.socket.send(KEEPALIVE_FRAME);
      } catch {
        /* the close handler picks it up */
      }
    }, KEEPALIVE_MS);
  }

  private disconnect(): void {
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onclose = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onopen = null;
      try {
        socket.close(1000, 'kraj');
      } catch {
        /* already gone */
      }
    }
    this.clearTimers();
  }

  private clearTimers(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.flushTimer) clearTimeout(this.flushTimer);
    if (this.keepaliveTimer) clearInterval(this.keepaliveTimer);
    this.retryTimer = null;
    this.flushTimer = null;
    this.keepaliveTimer = null;
  }

  /**
   * Come back, eventually.
   *
   * Doubling with a third of jitter, because without it every tab on the island reconnects in the
   * same second after a wobble and the room gets the one burst it was never going to enjoy.
   */
  private retry(): void {
    // `!this.islandId` alone was the test until a personal island became a second room kind, where
    // `islandId` is deliberately null — so every dropped socket there gave up permanently.
    if (this.stale || this.blocked || (!this.islandId && !this.dom)) {
      // Not coming back. The keepalive would otherwise tick on against a socket that no longer
      // exists for the rest of the screen's life.
      this.clearTimers();
      return;
    }
    if (this.retryTimer) return;
    const base = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** this.attempt);
    this.attempt += 1;
    const wait = base * (0.7 + Math.random() * 0.6);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, wait);
  }

  /* ---------------------------------------------------------------- talking */

  /**
   * Where the player is now, in world tiles. Called on every step and every turn.
   *
   * The conversion to an offset from the island's centre happens here and nowhere else — it is the
   * one thing that makes a position mean the same on two screens, so there is exactly one place it
   * can be got wrong.
   */
  report(tile: { x: number; y: number }, facing: Facing, moving: boolean): void {
    this.latest = {
      x: tile.x - this.anchor.x,
      y: tile.y - this.anchor.y,
      f: facingIndex(facing),
      m: moving ? 1 : 0,
    };
    this.flush(false);
  }

  /**
   * Write the current pose, unless it has already been written or it is too soon.
   *
   * `force` means "the room needs to hear this whatever the state of play" — it is used exactly
   * twice, on a fresh socket and when a woken room asks — and it bypasses both the has-not-changed
   * check and the rate window, because in both cases the room's idea of where we are is empty.
   */
  private flush(force: boolean): void {
    const next = this.latest;
    if (!next || this.socket?.readyState !== 1) return;

    if (!force && sameFrame(this.sent, next)) return;

    const now = this.clock();
    // The very first frame on a socket never waits: a member invisible for a fifth of a second
    // after every reconnect is a member who flickers on a bad line.
    const wait = this.sentAt === null ? 0 : SEND_MIN_MS - (now - this.sentAt);
    if (!force && wait > 0) {
      // The trailing edge. Whatever is suppressed now is sent the moment the window opens, so the
      // last frame of a walk always arrives — that is the one that must not be lost, because there
      // is nothing after it to correct where the body ended up.
      this.flushTimer ??= setTimeout(() => {
        this.flushTimer = null;
        this.flush(false);
      }, wait);
      return;
    }

    this.sentAt = now;
    this.sent = next;
    this.write({ t: 'p', ...next });
  }

  /**
   * One of the four. Broadcast to the island, never aimed at a person.
   *
   * Throttled here as well as in the room, because the room's answer to somebody sending too much
   * is to close the socket — and a member leaning on a number key would otherwise spend the frame
   * budget in a few seconds and drop everybody off their own map for their trouble.
   */
  gesture(index: number): void {
    if (this.socket?.readyState !== 1) return;
    const now = this.clock();
    if (now - this.lastGestureAt < GESTURE_MIN_MS) return;
    this.lastGestureAt = now;
    this.write({ t: 'g', g: index });
  }

  private lastGestureAt = -GESTURE_MIN_MS;

  private write(message: Parameters<typeof encode>[0]): void {
    try {
      this.socket?.send(encode(message));
    } catch {
      /* the close handler will pick this up */
    }
  }

  /* ---------------------------------------------------------------- listening */

  private receive(raw: unknown): void {
    const message = decodeFromRoom(raw as string);
    // Unrecognised is ordinary — see core/soba.ts. Nothing is logged; a bundle from before a
    // feature existed simply does not see it.
    if (!message) return;
    const now = this.clock();

    switch (message.t) {
      case 'soba':
        this.me = message.you;
        this.bodies.clear();
        for (const person of message.people) this.bodies.set(person.id, this.bodyOf(person));
        return;

      case 'dolazak': {
        if (message.person.id === this.me) return;
        const known = this.bodies.get(message.person.id);
        // Somebody already on screen can be announced again — the room re-announces everybody after
        // it has been evicted and asked where they are. Replacing the body would throw away the
        // steps it is part-way through and cut a gesture off mid-sentence, so this only refreshes
        // what the room is authoritative about and leaves the walk alone.
        if (known) {
          known.name = message.person.name;
          known.look = message.person.look;
          known.souvenirs = message.person.souvenirs;
          known.nodeId = message.person.nodeId;
          this.step(known, message.person.x, message.person.y, facingIndex(message.person.facing), now);
          return;
        }
        this.bodies.set(message.person.id, this.bodyOf(message.person));
        return;
      }

      case 'odlazak':
        this.bodies.delete(message.id);
        return;

      /**
       * Somebody changed how they look, mid-walk.
       *
       * Only the appearance is touched — not the position, not the queue, not the gesture floating
       * over their head. Replacing the body would drop whatever steps it is part-way through, which
       * would look like the person teleporting because they changed their shirt. The renderer bakes
       * off `look`, so the next frame draws the new one and there is nothing else to do.
       */
      case 'izgled': {
        const known = this.bodies.get(message.id);
        if (known) known.look = message.look;
        return;
      }

      case 'p': {
        const body = this.bodies.get(message.id);
        if (!body) return;
        this.step(body, message.x, message.y, message.f, now);
        return;
      }

      case 'dvoboj':
        // A kind this file did not have last month, and the reason rule 1 exists. An older bundle
        // drops it in `decodeFromRoom` and never gets here.
        if (message.ev === 'poziv') this.onDuel?.(message.duel);
        return;

      case 'g': {
        const body = this.bodies.get(message.id);
        const gest = gestOf(message.g);
        if (!body || !gest) return;
        body.gesture = { gest, until: now + GESTURE_MS };
        return;
      }

      case 'gde':
        // The room woke up and does not know where anybody is. Answer immediately, past the
        // throttle: this is the one frame that decides whether we exist to everyone else.
        this.flush(true);
        return;

      case 'kraj':
        this.hangUp(message.reason);
        return;
    }
  }

  private hangUp(reason: KrajReason): void {
    if (reason === 'verzija') this.stale = true;
    // `zamenjen` is in here and `zatvoreno` is not, and that is the whole distinction: the room
    // allows one body per member, so a tab that comes back after being replaced evicts the tab that
    // replaced it, which comes back and evicts this one, twice a second, forever. The newest tab is
    // the one somebody is looking at, so it wins and this one stops.
    if (reason === 'zakljucano' || reason === 'van-posade' || reason === 'zamenjen')
      this.blocked = true;
    // `zatvoreno` and `previse` fall through: the close handler retries, with the backoff.
  }

  private bodyOf(person: Present): Body {
    const x = this.anchor.x + person.x;
    const y = this.anchor.y + person.y;
    return {
      id: person.id,
      name: person.name,
      look: person.look,
      souvenirs: person.souvenirs,
      nodeId: person.nodeId,
      fromX: x,
      fromY: y,
      tileX: x,
      tileY: y,
      progress: 1,
      facing: person.facing,
      stepCount: 0,
      gesture: null,
      queue: [],
    };
  }

  /**
   * A position arrived. Walk to it, turn towards it, or simply be there.
   *
   * Three cases, and the middle one is the one that is easy to miss: a member who turns on the spot
   * sends a frame whose tile has not changed, and animating that as a step would make everybody
   * twitch every time somebody looked around.
   */
  private step(body: Body, offsetX: number, offsetY: number, facingIdx: number, now: number): void {
    const facing = FACING_BY_INDEX[facingIdx] ?? body.facing;
    const x = this.anchor.x + offsetX;
    const y = this.anchor.y + offsetY;

    const last = body.queue.length ? body.queue[body.queue.length - 1]! : { x: body.tileX, y: body.tileY };

    if (last.x === x && last.y === y) {
      // Turned in place.
      if (body.queue.length) body.queue[body.queue.length - 1]!.facing = facing;
      else body.facing = facing;
      return;
    }

    if (walkable(last, { x, y }) && body.queue.length < SNAP_AT) {
      body.queue.push({ x, y, facing });
      return;
    }

    // Too far to have been walked, or too far behind to be worth pretending. A body dragged
    // sideways through a cliff to catch up looks far worse than one that was simply somewhere else.
    body.queue.length = 0;
    body.fromX = x;
    body.fromY = y;
    body.tileX = x;
    body.tileY = y;
    body.progress = 1;
    body.facing = facing;
  }

  /* ---------------------------------------------------------------- the clock */

  /**
   * Spend a frame: walk everybody a little further, and forget whoever has gone quiet.
   *
   * The loop is the same shape as the engine's own: a step that completes mid-frame hands the rest
   * of the frame to the next one, so a body with three steps queued does not stand still for a
   * frame at every tile edge.
   */
  advance(delta: number, now = this.clock()): void {
    for (const body of this.bodies.values()) {
      if (body.gesture && now >= body.gesture.until) body.gesture = null;

      let remaining = delta;
      for (let guard = 0; guard < 6; guard++) {
        const stepMs = body.queue.length >= HURRY_AT ? STEP_MS * 0.62 : STEP_MS;
        if (body.progress < 1) {
          const cost = (1 - body.progress) * stepMs;
          if (remaining < cost) {
            body.progress += remaining / stepMs;
            break;
          }
          remaining -= cost;
          body.progress = 1;
          body.fromX = body.tileX;
          body.fromY = body.tileY;
        }
        const next = body.queue.shift();
        if (!next) break;
        body.fromX = body.tileX;
        body.fromY = body.tileY;
        body.tileX = next.x;
        body.tileY = next.y;
        body.facing = next.facing;
        body.progress = 0;
        body.stepCount += 1;
      }
    }
  }

  /* ---------------------------------------------------------------- reading */

  /** Everybody the map should draw. Never includes the player themselves. */
  people(): LiveBody[] {
    return [...this.bodies.values()];
  }

  /** True while this member is drawn as a walking body, so their standing ghost can be dropped. */
  has(id: string): boolean {
    return this.bodies.has(id);
  }

  ids(): Set<string> {
    return new Set(this.bodies.keys());
  }

  find(id: string): LiveBody | undefined {
    return this.bodies.get(id);
  }

  /**
   * Somebody standing on, or stepping onto, this tile.
   *
   * Both tiles count while a step is in flight. A body between two tiles that answered to neither
   * would be a person you can see and cannot talk to, and which of the two is "really" theirs
   * changes sixty times a second — so the answer is both, and the closer one wins.
   */
  at(x: number, y: number): LiveBody | undefined {
    let best: LiveBody | undefined;
    let bestDistance = Infinity;
    for (const body of this.bodies.values()) {
      const onTarget = body.tileX === x && body.tileY === y;
      const onOrigin = body.fromX === x && body.fromY === y && body.progress < 1;
      if (!onTarget && !onOrigin) continue;
      // Whichever half of the step they are further into. Ties break on id so two people on one
      // tile always resolve the same way for everybody looking at them.
      const distance = onTarget ? 1 - body.progress : body.progress;
      if (distance < bestDistance || (distance === bestDistance && best && body.id < best.id)) {
        best = body;
        bestDistance = distance;
      }
    }
    return best;
  }

  /** Interpolated position in tiles, for drawing and for depth. */
  static pixelOf(body: LiveBody): { x: number; y: number } {
    return {
      x: body.fromX + (body.tileX - body.fromX) * body.progress,
      y: body.fromY + (body.tileY - body.fromY) * body.progress,
    };
  }
}

const FACING_BY_INDEX: readonly Facing[] = ['up', 'down', 'left', 'right'];

type Frame = { x: number; y: number; f: number; m: 0 | 1 };

const sameFrame = (a: Frame | null, b: Frame): boolean =>
  a !== null && a.x === b.x && a.y === b.y && a.f === b.f && a.m === b.m;
